/**
 * Billing Service for Admin Dashboard
 * 
 * Handles billing operations for families including invoices, subscriptions, and credits.
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from './firebase/config';

// Initialize Functions - use default region (us-central1 for v2 functions)
// If your functions are in a different region, specify it: getFunctions(app, 'us-east1')
const functions = getFunctions(app);

// Connect to emulator in development if needed
if (import.meta.env.DEV && import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.log('🔧 Connected to Functions Emulator at localhost:5001');
} else if (import.meta.env.DEV) {
  console.log('ℹ️  Using production Firebase Functions');
  console.log('   To use emulator, set VITE_USE_FUNCTIONS_EMULATOR=true');
}

export interface Invoice {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: string;
  created: number;
  dueDate: number | null;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
}

export interface UpcomingInvoice {
  amount: number;
  currency: string;
  periodStart: number;
  periodEnd: number;
}

export interface Subscription {
  id: string;
  status: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
}

export interface BillingHistory {
  invoices: Invoice[];
  upcomingInvoice: UpcomingInvoice | null;
  subscriptions: Subscription[];
}

/**
 * Get billing history for a family
 */
export const getBillingHistory = async (familyId: string): Promise<BillingHistory> => {
  try {
    console.log('📞 Calling adminGetBillingHistory for familyId:', familyId);
    const getBillingHistoryFn = httpsCallable(functions, 'adminGetBillingHistory');
    
    // Add timeout and better error handling
    const result = await Promise.race([
      getBillingHistoryFn({ familyId }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Function call timed out after 30 seconds')), 30000)
      )
    ]) as any;
    
    console.log('✅ Billing history retrieved successfully');
    return result.data as BillingHistory;
  } catch (error: any) {
    console.error('❌ Error fetching billing history:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error details:', error.details);
    
    // Check if it's a network/CORS error
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      throw new Error('Network error. Check if the function is deployed and accessible. Error: ' + error.message);
    }
    
    // Check for CORS errors
    if (error.message?.includes('CORS') || error.message?.includes('cors') || error.code === 'functions/cors') {
      throw new Error('CORS error. The function may not be deployed or CORS is not properly configured. Make sure the function is deployed with CORS enabled.');
    }
    
    // Provide more detailed error information
    if (error.code === 'functions/not-found' || error.code === 'not-found' || error.message?.includes('404')) {
      throw new Error('Function not found (404). Please ensure the Firebase Functions are deployed. Run: firebase deploy --only functions');
    }
    if (error.code === 'functions/unavailable' || error.code === 'unavailable') {
      throw new Error('Functions service is unavailable. Please check your Firebase configuration and network connection.');
    }
    if (error.code === 'functions/permission-denied' || error.code === 'permission-denied') {
      throw new Error('Permission denied. You must be an admin to access billing history.');
    }
    if (error.code === 'functions/unauthenticated' || error.code === 'unauthenticated') {
      throw new Error('Authentication required. Please sign in again.');
    }
    
    const errorMessage = error.message || error.details || 'Failed to fetch billing history';
    throw new Error(errorMessage);
  }
};

/**
 * Download invoice PDF URL
 */
export const getInvoiceDownloadUrl = async (invoiceId: string): Promise<{ invoicePdf: string | null; hostedInvoiceUrl: string | null }> => {
  try {
    const downloadInvoiceFn = httpsCallable(functions, 'adminDownloadInvoice');
    const result = await downloadInvoiceFn({ invoiceId });
    return result.data as { invoicePdf: string | null; hostedInvoiceUrl: string | null };
  } catch (error: any) {
    console.error('Error downloading invoice:', error);
    throw new Error(error.message || 'Failed to download invoice');
  }
};

/**
 * Cancel subscription for a family
 */
export const cancelSubscription = async (
  familyId: string,
  cancelImmediately: boolean = false
): Promise<{ success: boolean; canceled: boolean }> => {
  try {
    const cancelSubscriptionFn = httpsCallable(functions, 'adminCancelSubscription');
    const result = await cancelSubscriptionFn({ familyId, cancelImmediately });
    return result.data as { success: boolean; canceled: boolean };
  } catch (error: any) {
    console.error('Error canceling subscription:', error);
    throw new Error(error.message || 'Failed to cancel subscription');
  }
};

/**
 * Apply credit to a customer account
 */
export const applyCredit = async (
  familyId: string,
  amount: number,
  currency: string = 'usd',
  description?: string
): Promise<{ success: boolean; transactionId: string; amount: number; currency: string }> => {
  try {
    const applyCreditFn = httpsCallable(functions, 'adminApplyCredit');
    const result = await applyCreditFn({ familyId, amount, currency, description });
    return result.data as { success: boolean; transactionId: string; amount: number; currency: string };
  } catch (error: any) {
    console.error('Error applying credit:', error);
    throw new Error(error.message || 'Failed to apply credit');
  }
};

